import { useState, useMemo } from 'react';
import {
  Loader2,
  FileText,
  BarChart3,
  DollarSign,
  Percent,
  TrendingUp,
  BedDouble,
  Download,
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/ui/StatCard';
import { SlideOver } from '@/components/ui/SlideOver';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatMoney } from '@/lib/money';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useReportsLibrary, useGenerateReport, useStatistics } from '@/hooks/api/useReports';
import { downloadReportExport } from '@/services/api/reports.api';
import type {
  ReportType,
  ReportDefinition,
  ReportResult,
} from '@/services/api/reports.api';

// ─── Category labels ─────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  // Categories as seeded by the backend report library
  front_desk: 'Front Desk',
  housekeeping: 'Housekeeping',
  operations: 'Operations',
  revenue: 'Revenue & Occupancy',
  // Legacy / alternate category keys
  enterprise_audit: 'Enterprise & Audit',
  revenue_occupancy: 'Revenue & Occupancy',
  housekeeping_maintenance: 'Housekeeping & Maintenance',
  reservations: 'Reservations',
  activity_booking: 'Activity & Booking',
  companies: 'Companies',
  downtime: 'Downtime',
};

// ─── Page ────────────────────────────────────────────────────────────────────

export function ReportsLibraryPage() {
  const propertyId = useSelectedPropertyId();
  const [fromDate] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [toDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [generatePanel, setGeneratePanel] = useState<ReportDefinition | null>(null);
  const [reportResult, setReportResult] = useState<ReportResult | null>(null);

  const { data: libraryRaw, isLoading: libraryLoading } = useReportsLibrary();
  const { data: statsData } = useStatistics({ propertyId, from: fromDate, to: toDate });
  const generateMutation = useGenerateReport();

  const libraryCategories = useMemo(() => {
    const items = Array.isArray(libraryRaw) ? libraryRaw : (libraryRaw as any)?.data ?? [];
    const map = new Map<string, ReportDefinition[]>();
    for (const r of items) {
      const list = map.get(r.category) || [];
      list.push(r);
      map.set(r.category, list);
    }
    return Array.from(map.entries()).map(([category, reports]) => ({
      category,
      categoryLabel: '',
      reports,
    }));
  }, [libraryRaw]);

  const handleGenerate = (reportType: ReportType, genFrom: string, genTo: string) => {
    generateMutation.mutate(
      { reportType, propertyId, from: genFrom, to: genTo },
      {
        onSuccess: (result) => {
          setReportResult(result);
        },
      },
    );
  };

  return (
    <div>
      <PageHeader
        title="Reports"
        description="Generate and view reports for your properties"
      />

      {/* KPI cards */}
      {statsData && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={DollarSign}
            value={formatMoney(statsData.revenue)}
            label="Total Revenue"
            borderColor="border-l-green-500"
          />
          <StatCard
            icon={Percent}
            value={`${statsData.occupancyRate}%`}
            label="Occupancy"
            borderColor="border-l-blue-500"
          />
          <StatCard
            icon={TrendingUp}
            value={formatMoney(statsData.adr)}
            label="ADR"
            borderColor="border-l-purple-500"
          />
          <StatCard
            icon={BedDouble}
            value={formatMoney(statsData.revPar)}
            label="RevPAR"
            borderColor="border-l-orange-500"
          />
        </div>
      )}

      {/* Reports library */}
      {libraryLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : libraryCategories.length === 0 ? (
        <EmptyState icon={FileText} title="No reports available" description="Report definitions will appear here once configured." />
      ) : (
        <div className="space-y-8">
          {libraryCategories.map((cat) => (
            <div key={cat.category}>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {cat.categoryLabel || CATEGORY_LABELS[cat.category] || cat.category}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat.reports.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    onGenerate={() => setGeneratePanel(report)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate side panel */}
      <SlideOver
        isOpen={generatePanel !== null}
        onClose={() => {
          setGeneratePanel(null);
          setReportResult(null);
        }}
        title={generatePanel?.name ?? 'Generate Report'}
        width="max-w-xl"
      >
        {generatePanel !== null && (
          <GenerateReportPanel
            report={generatePanel}
            propertyId={propertyId}
            onGenerate={handleGenerate}
            isGenerating={generateMutation.isPending}
            result={reportResult}
          />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Report card ─────────────────────────────────────────────────────────────

function ReportCard({ report, onGenerate }: { report: ReportDefinition; onGenerate: () => void }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col justify-between">
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <FileText size={16} className="text-gray-400 shrink-0" aria-hidden="true" />
          <h4 className="text-sm font-semibold text-gray-900">{report.name}</h4>
        </div>
        <p className="text-xs text-gray-500">{report.reportType.replace(/_/g, ' ')}</p>
      </div>
      <Button size="sm" variant="secondary" onClick={onGenerate}>
        <BarChart3 size={14} aria-hidden="true" />
        Generate
      </Button>
    </div>
  );
}

// ─── Result normalization ────────────────────────────────────────────────────

/**
 * The /reports/generate `data` payload is report-type specific. Normalize it
 * into a flat scalar summary + an optional row list so the panel can render any
 * shape without assuming a `summary`/array (which previously crashed it).
 */
function normalizeReportData(data: unknown): {
  message: string | null;
  summary: Array<[string, unknown]>;
  rows: Record<string, unknown>[];
} {
  if (data == null) {
    return { message: 'No data for the selected period.', summary: [], rows: [] };
  }
  if (Array.isArray(data)) {
    return { message: null, summary: [], rows: data as Record<string, unknown>[] };
  }
  if (typeof data !== 'object') {
    return { message: String(data), summary: [], rows: [] };
  }
  const obj = data as Record<string, unknown>;
  if (typeof obj.error === 'string') {
    return { message: obj.error, summary: [], rows: [] };
  }
  const summary: Array<[string, unknown]> = [];
  let rows: Record<string, unknown>[] = [];
  for (const [key, val] of Object.entries(obj)) {
    if (Array.isArray(val)) {
      if (rows.length === 0) rows = val as Record<string, unknown>[];
    } else if (val !== null && typeof val === 'object') {
      for (const [k2, v2] of Object.entries(val as Record<string, unknown>)) {
        summary.push([`${key}.${k2}`, v2]);
      }
    } else {
      summary.push([key, val]);
    }
  }
  const message =
    summary.length === 0 && rows.length === 0
      ? 'No data for the selected period.'
      : null;
  return { message, summary, rows };
}

// ─── Generate panel ──────────────────────────────────────────────────────────

function GenerateReportPanel({
  report,
  propertyId,
  onGenerate,
  isGenerating,
  result,
}: {
  report: ReportDefinition;
  propertyId: string;
  onGenerate: (type: ReportType, from: string, to: string) => void;
  isGenerating: boolean;
  result: ReportResult | null;
}) {
  const [from, setFrom] = useState(() => format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [to, setTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [exportingFormat, setExportingFormat] = useState<'csv' | 'json' | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate(report.reportType, from, to);
  };

  const handleExport = async (fmt: 'csv' | 'json') => {
    setExportingFormat(fmt);
    try {
      await downloadReportExport(report.reportType, {
        from,
        to,
        propertyId,
        format: fmt,
      });
    } catch {
      // Download failed — user will see the loading state clear
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="rpt-from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input id="rpt-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor="rpt-to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input id="rpt-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="submit" isLoading={isGenerating} fullWidth>
            Generate Report
          </Button>
        </div>
      </form>

      {/* Export buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          isLoading={exportingFormat === 'csv'}
          disabled={exportingFormat !== null}
          onClick={() => handleExport('csv')}
        >
          <Download size={14} aria-hidden="true" />
          Export CSV
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          isLoading={exportingFormat === 'json'}
          disabled={exportingFormat !== null}
          onClick={() => handleExport('json')}
        >
          <Download size={14} aria-hidden="true" />
          Export JSON
        </Button>
      </div>

      {/* Results */}
      {result && (() => {
        const { message, summary, rows } = normalizeReportData(result.data);
        return (
          <div className="space-y-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-700">Results</h4>
              <span className="text-xs text-gray-400">Generated {new Date(result.generatedAt).toLocaleString()}</span>
            </div>

            {/* Message (empty data / unimplemented type) */}
            {message && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">{message}</div>
            )}

            {/* Summary */}
            {summary.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase">Summary</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {summary.map(([key, val]) => (
                    <div key={key}>
                      <span className="text-gray-500">{key.replace(/[_.]/g, ' ')}:</span>{' '}
                      <span className="font-medium text-gray-900">{String(val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Data table */}
            {rows.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      {Object.keys(rows[0]!).map((col) => (
                        <th key={col} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                          {col.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.slice(0, 50).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {Object.values(row).map((val, j) => (
                          <td key={j} className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {val !== null && typeof val === 'object' ? JSON.stringify(val) : String(val ?? '--')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
